import React, { ComponentPropsWithRef, ForwardedRef, MutableRefObject, ReactNode, forwardRef, useEffect, useRef } from 'react';
import cx from 'classnames';
import type { OptionGroup, OptionProp, Options } from '../shared/normalize-options';
import { TypeaheadFlosField, type FlosFieldProps } from '../../flos-field';
import { FlosInput, type FlosInputProps } from '../../input/flos-input';
import { usePointerDownOutside } from './hooks/use-pointer-down-outside';
import { useTypeaheadController } from './hooks/use-typeahead-controller';
import type { TypeaheadMatchFn } from './hooks/use-typeahead-filtering';
import { TypeaheadDropdown } from './components/typeahead-dropdown';
import type { DomEnvironment, DropdownCloseStrategy, TypeaheadDropdownApi, TypeaheadInteractionContext, TypeaheadValidationState, TypeaheadValidationStrategy } from './types';

type TypeaheadRenderEmpty = (payload: { query: string }) => ReactNode;
type TypeaheadRenderLoading = TypeaheadRenderEmpty;

type TypeaheadRenderOption = (payload: {
    option: OptionProp;
    group: OptionGroup;
    index: number;
    isActive: boolean;
    isSelected: boolean;
    props: React.LiHTMLAttributes<HTMLLIElement>;
}) => ReactNode;

type TypeaheadRenderGroupHeader = (payload: { group: OptionGroup }) => ReactNode;

type TypeaheadFieldProps = {
    /**
     * options that will be rendered as children, you can also construct your option markup as children
     */
    options: Options;
    value?: string;
    matchOption?: TypeaheadMatchFn;
    renderEmpty?: TypeaheadRenderEmpty;
    renderLoading?: TypeaheadRenderLoading;
    renderOption?: TypeaheadRenderOption;
    renderGroupHeader?: TypeaheadRenderGroupHeader;
    /**
     * @deprecated Prefer using `interactionContext.onInputChange`. This callback will be removed in a future release.
     */
    onInputValueChange?: (value: string) => void;
    isLoading?: boolean;
    loadingText?: string;
    validateDisplayValue?: (value: string, context: { options: OptionProp[]; selectedOption?: OptionProp }) => boolean;
    preserveSelectedOption?: boolean;
    onDropdownToggle?: (isOpen: boolean) => void;
    onReset?: () => void;
    inputRef?: React.Ref<HTMLInputElement>;
    openOnFocus?: boolean;
    closeOnSelect?: boolean | ((option: OptionProp) => boolean);
    dropdownApiRef?: MutableRefObject<TypeaheadDropdownApi | null>;
    shouldAutoOpen?: (value: string) => boolean;
    validationStrategy?: TypeaheadValidationStrategy;
    dropdownCloseStrategy?: DropdownCloseStrategy;
    environment?: DomEnvironment;
    interactionContext?: TypeaheadInteractionContext;
    onValidationStateChange?: (state: TypeaheadValidationState) => void;
} & Omit<FlosInputProps, 'placeholder' | 'onChange'> &
    Omit<FlosFieldProps, 'onChange'> &
    Omit<ComponentPropsWithRef<'input'>, 'onBlur' | 'placeholder'>;

const TypeaheadField = forwardRef<HTMLInputElement, Omit<TypeaheadFieldProps, 'maxLength'>>(
    (
        {
            options,
            value,
            matchOption,
            renderEmpty,
            renderLoading,
            renderOption,
            renderGroupHeader,
            onInputValueChange,
            isValid,
            isDirty: _ignoredIsDirty,
            onValidityChange,
            label,
            iconShape,
            errorText,
            helpText,
            onFocus,
            onBlur,
            onChange,
            wrapperClassName,
            isLoading,
            loadingText,
            validateDisplayValue,
            preserveSelectedOption,
            onDropdownToggle,
            onReset,
            inputRef,
            openOnFocus = true,
            closeOnSelect = true,
            dropdownApiRef,
            shouldAutoOpen,
            validationStrategy,
            dropdownCloseStrategy,
            environment,
            interactionContext,
            onValidationStateChange,
            validationState: _ignoredValidationState,
            ...inputProps
        },
        forwardedRef: ForwardedRef<HTMLInputElement>
    ) => {
        void _ignoredIsDirty;
        void _ignoredValidationState;
        const wrapperRef = useRef<HTMLDivElement>(null);
        const legacyInputWarningRef = useRef(false);

        useEffect(() => {
            if (process.env.NODE_ENV !== 'production' && onInputValueChange && !legacyInputWarningRef.current && interactionContext) {
                legacyInputWarningRef.current = true;
                console.warn('TypeaheadField: `onInputValueChange` is deprecated. Rely on `interactionContext.onInputChange` instead to avoid duplicate callbacks.');
            }
        }, [interactionContext, onInputValueChange]);
        const controller = useTypeaheadController({
            forwardedRef,
            options,
            value,
            matchOption,
            preserveSelectedOption,
            onInputValueChange,
            onHiddenInputChange: onChange,
            validationStrategy,
            legacyValidationAdapter: validateDisplayValue,
            dropdownCloseStrategy,
            legacyCloseOnSelect: closeOnSelect,
            onDropdownToggle,
            onReset,
            externalInputRef: inputRef,
            dropdownApiRef,
            openOnFocus,
            shouldAutoOpen,
            isValidProp: isValid,
            environment,
            onFocus,
            onBlur,
            wrapperRef,
            interactionContext,
            onValidationStateChange,
        });

        usePointerDownOutside({
            ref: wrapperRef,
            enabled: controller.state.isDropdownOpen,
            handler: () => {
                controller.closeDropdown();
                controller.runValidation(controller.state.inputValue, 'blur');
            },
        });

        const renderedDropdown = controller.state.isDropdownOpen ? (
            <TypeaheadDropdown
                listboxId={controller.dropdown.listboxId}
                query={controller.dropdown.query}
                groups={controller.dropdown.groups}
                flattenedOptions={controller.dropdown.flattenedOptions}
                optionIndexByValue={controller.dropdown.optionIndexByValue}
                activeIndex={controller.dropdown.activeIndex}
                selectedOption={controller.dropdown.selectedOption}
                onOptionPointerDown={controller.handlers.handleOptionPointerDown}
                onOptionHover={controller.handlers.handleOptionHover}
                onOptionFocus={controller.handlers.handleOptionFocus}
                onOptionKeyDown={controller.handlers.handleOptionKeyDown}
                renderEmpty={renderEmpty}
                isLoading={isLoading}
                renderLoading={renderLoading}
                loadingText={loadingText}
                renderOption={renderOption}
                renderGroupHeader={renderGroupHeader}
            />
        ) : null;

        return (
            <div className={cx('typeaheadfield', controller.state.isDropdownOpen && 'is-open')} ref={wrapperRef}>
                <TypeaheadFlosField
                    onValidityChange={onValidityChange}
                    validationState={controller.state.validation}
                    label={label}
                    iconShape={iconShape}
                    errorText={errorText}
                    required={inputProps.required}
                    hasDropdown={true}
                    id={inputProps.id}
                    helpText={helpText}
                    disabled={inputProps.disabled}
                    resetHandler={controller.handlers.handleReset}
                    wrapperClassName={wrapperClassName}
                    ref={controller.hiddenInput.hiddenInputRef}
                    renderInput={({ getInputProps }) => (
                        <>
                            <FlosInput
                                {...getInputProps({
                                    ...inputProps,
                                    onChange: controller.handlers.handleInputChange,
                                    onFocus: controller.handlers.handleInputFocus,
                                    onBlur: controller.handlers.handleInputBlur,
                                    onKeyDown: controller.handlers.handleInputKeyDown,
                                })}
                                value={controller.state.inputValue}
                                role="combobox"
                                aria-autocomplete="list"
                                aria-expanded={controller.state.isDropdownOpen}
                                aria-controls={controller.dropdown.listboxId}
                                aria-activedescendant={controller.state.activeOptionId}
                                autoComplete="off"
                                ref={controller.mergeDisplayInputRef}
                            />
                            {renderedDropdown}
                        </>
                    )}
                />

                <input onChange={controller.hiddenInput.handleHiddenChange} className="visuallyhidden" ref={controller.hiddenInput.hiddenInputRef} tabIndex={-1} />
            </div>
        );
    }
);

TypeaheadField.displayName = 'TypeaheadField';

export type { TypeaheadFieldProps, TypeaheadDropdownApi };
export type { TypeaheadRenderEmpty, TypeaheadRenderGroupHeader, TypeaheadRenderOption, TypeaheadRenderLoading };
export { TypeaheadField };
export default TypeaheadField;
